/** Everyone at the company who has, or will have, a ryuauth account. */

import { useState } from "react";
import { UserPlus, UserX } from "lucide-react";
import { Button } from "@/components/button";
import { Dialog } from "@/components/dialog";
import { Avatar, PageHeader, panelClass, StatusBadge } from "@/components/display";
import { InlineSelect } from "@/components/fields";
import { Empty, LoadError, Loading, useReportError } from "@/components/states";
import { useMe } from "@/features/session/hooks";
import type { OrgRole, Person } from "@/features/session/types";
import { AddPersonDialog } from "./add-person-dialog";
import { useOffboard, usePeople, useSetOrgRole } from "./hooks";

export function PeoplePage() {
  const { data: people, isPending, isError, error } = usePeople();
  const { data: me } = useMe();
  const setRole = useSetOrgRole();
  const offboard = useOffboard();
  const reportError = useReportError();
  const [adding, setAdding] = useState(false);
  const [leaving, setLeaving] = useState<Person | null>(null);

  const addButton = (
    <Button tone="primary" onClick={() => setAdding(true)}>
      <UserPlus size={16} /> Add person
    </Button>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="People"
        description="Org roles apply in every product. Deactivating someone signs them out everywhere."
        action={addButton}
      />
      <div className={panelClass}>
        {isPending ? (
          <Loading what="people" />
        ) : isError ? (
          <LoadError what="people" error={error} />
        ) : people.length === 0 ? (
          <Empty title="No one here yet" action={addButton}>
            Add the people who should be able to sign in. They're linked to Microsoft on first sign-in.
          </Empty>
        ) : (
          <table className="w-full table-fixed text-left text-sm">
            <colgroup>
              <col />
              <col className="w-32 sm:w-36" />
              <col className="hidden w-44 sm:table-column" />
              <col className="w-12 sm:w-32" />
            </colgroup>
            <thead className="border-b border-zinc-200 bg-zinc-50/80 text-xs font-medium text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/80">
              <tr>
                <th className="px-4 py-2.5 font-medium sm:px-5">Person</th>
                <th className="px-2 py-2.5 font-medium sm:px-3">Org role</th>
                <th className="hidden px-3 py-2.5 font-medium sm:table-cell">Status</th>
                <th className="px-2 py-2.5 sm:px-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {people.map((person) => (
                <PersonRow
                  key={person.sub}
                  person={person}
                  isMe={person.sub === me?.sub}
                  onRole={(role) => setRole.mutate({ sub: person.sub, role }, { onError: reportError })}
                  onOffboard={() => setLeaving(person)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AddPersonDialog open={adding} onClose={() => setAdding(false)} />
      <Dialog
        open={leaving !== null}
        onClose={() => setLeaving(null)}
        title={`Deactivate ${leaving?.name || leaving?.email}?`}
        description="They're removed from every workspace and signed out of every product within 10 minutes. This can't be undone from the console."
        footer={
          <>
            <Button onClick={() => setLeaving(null)}>Keep access</Button>
            <Button
              tone="danger"
              onClick={() => {
                if (leaving) offboard.mutate(leaving.sub, { onError: reportError });
                setLeaving(null);
              }}
            >
              Deactivate
            </Button>
          </>
        }
      />
    </div>
  );
}

function PersonRow({ person, isMe, onRole, onOffboard }: {
  person: Person;
  isMe: boolean;
  onRole: (role: OrgRole) => void;
  onOffboard: () => void;
}) {
  const status = !person.active ? "inactive" : person.signed_in_before ? "active" : "invited";
  const statusLabel = { active: "Active", invited: "Not signed in yet", inactive: "Deactivated" }[status];
  return (
    <tr className={person.active ? "" : "text-zinc-400"}>
      <td className="px-4 py-3 sm:px-5">
        <div className="flex items-center gap-3">
          <Avatar name={person.name} email={person.email} />
          <div className="min-w-0">
            <p className="truncate font-medium text-zinc-900 dark:text-zinc-100">
              {person.name || person.email.split("@")[0]}
              {isMe && <span className="ml-1.5 text-xs font-normal text-zinc-500">(you)</span>}
            </p>
            <p className="truncate text-xs text-zinc-500">{person.email}</p>
            <p className="mt-1 sm:hidden"><StatusBadge status={status}>{statusLabel}</StatusBadge></p>
          </div>
        </div>
      </td>
      <td className="py-3">
        <InlineSelect value={person.org_role} disabled={!person.active || isMe}
          onChange={(e) => onRole(e.target.value as OrgRole)} aria-label={`Org role for ${person.email}`}>
          <option value="member">Member</option>
          <option value="admin">Admin</option>
          <option value="superadmin">Superadmin</option>
        </InlineSelect>
      </td>
      <td className="hidden px-3 py-3 sm:table-cell"><StatusBadge status={status}>{statusLabel}</StatusBadge></td>
      <td className="px-2 py-3 text-right sm:px-3">
        {person.active && !isMe && (
          <Button tone="ghost" onClick={onOffboard} aria-label={`Deactivate ${person.email}`}
            className="hover:text-red-600 max-sm:w-9 max-sm:px-0 dark:hover:text-red-400">
            <UserX size={16} className="sm:hidden" />
            <span className="max-sm:sr-only">Deactivate</span>
          </Button>
        )}
      </td>
    </tr>
  );
}
